import { MaintenanceRequester } from '@cobliteam/api-lib';
import type { CreateCheckpointManualInput, VehicleOdometer } from '@cobliteam/api-lib/src/__generated__/graphql';
import { GraphQLOdometerRequester } from '@cobliteam/api-lib/src/dashboard/odometer';
import type { UUID } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getMaintenanceRequester = () =>
  new MaintenanceRequester({
    host: env.herbieApiHost,
  });

const getGraphQLOdometerRequester = () => new GraphQLOdometerRequester();

const QUERY_KEY = 'VEHICLE_ODOMETER';

const useVehicleOdometer = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('vehicles');

  // A vehicle still being created has no id yet; the query stays idle instead of being skipped conditionally.
  const useOdometer = (vehicleId: UUID | undefined) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<VehicleOdometer>({
      queryKey: [QUERY_KEY, vehicleId],
      queryFn: () => getMaintenanceRequester().getVehicleOdometer(vehicleId as UUID),
      enabled: !!vehicleId,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });
  };

  const postOdometer = useMutation<VehicleOdometer, unknown, { vehicleId: UUID; currentOdometerInKm: number; odometerDateAt: number }>({
    mutationFn: (variables) => getMaintenanceRequester().createVehicleOdometer(variables.vehicleId, variables.currentOdometerInKm, variables.odometerDateAt),
    onSuccess: (data, variables) => {
      queryClient.setQueryData([QUERY_KEY, variables.vehicleId], data);
    },
  });

  const createOdometer = useMutation({
    mutationFn: async (request: CreateCheckpointManualInput) => await getGraphQLOdometerRequester().createCheckpointManual(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY],
      });
    },
    onError: () => {
      Alert.error(t('messages.errors.save_odometer'));
    },
  });

  return {
    useOdometer,
    postOdometer,
    createOdometer,
  };
};

export default useVehicleOdometer;
